import React, { ReactNode, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useMedia } from 'react-use';

import {
  ALLOWED_MIME_TYPES,
  DOCUMENT_TYPES,
  IMAGE_DOCUMENT_TYPES,
  media,
  MIME_TYPES,
} from '@savgroup-front-common/constants';
import { MessageType } from '@savgroup-front-common/types';

import { CommonAttachmentService } from '../../../api';
import {
  FileUploadHookForm,
  NewDesignFileUploadHookForm,
} from '../../../atoms/Form';
import { useWarningFormContext } from '../../../atoms/Form/Form/WarningFormProvider';
import { toast } from '../../../atoms/Toast';
import { safeFormattedIntlString } from '../../../formatters';
import { delay } from '../../../helpers';
import { useToasts } from '../../../molecules/NotificationsProvider';
import { useAdditionalInformationContext } from '../AdditionalInformation.context';
import { QrCodeImportModal } from '../components/QrCodeImportModal/QrCodeImportModal';

import { verifyThatShouldRecognizeSerialNumber } from './helpers/verifyThatShouldRecognizeSerialNumber';
import { useProcessNamePlateForSerialNumber } from './hooks/useProcessNamePlateForSerialNumber.hooks';
import messages from './messages';

interface AdditionalInformationFileInputProps {
  name: string;
  label?: MessageType | string;
  postLabel: string | ReactNode;
  value: { value: File; progress?: number };
  onChange: (files?: File[]) => void;
  onRemove: (file: { value: File; progress?: number }) => void;
  isRequired: boolean;
  isLiveUpload?: boolean;
  isNewDesign: boolean;
  isImportByQrCode: boolean;
  additionalInformationId: string;
  dataTestId: string;
  claimIds: string[];
  documentType?: DOCUMENT_TYPES;
}

const AdditionalInformationFileInput: React.FC<
  AdditionalInformationFileInputProps
> = ({
  name,
  label,
  postLabel,
  value,
  onChange,
  onRemove,
  isRequired,
  isLiveUpload,
  isNewDesign,
  isImportByQrCode,
  additionalInformationId,
  dataTestId,
  claimIds,
  documentType,
}) => {
  const isMobileView = useMedia(media.maxWidth.xs);
  const formContext = useFormContext();
  const {
    setValue,
    formState: { errors },
    getValues,
  } = formContext;

  const { neededInformation: neededInformationItems, prefixFormName } =
    useAdditionalInformationContext();
  const { warnings } = useWarningFormContext();

  const { pushError } = useToasts();
  const { fileId } = useParams<{ fileId?: string }>();

  const [isOpen, setIsOpen] = useState(false);

  const { processNamePlateForSerialNumber, isProcessingNamePlate } =
    useProcessNamePlateForSerialNumber({
      claimIds,
    });

  const { mutateAsync: handleUploadFinishClick, isLoading: isUploadLoading } =
    useMutation(
      ['syncrhonizeFileAdditionalInformationForm'],
      async ({ sessionId }: { sessionId?: string }) => {
        if (!sessionId) {
          setIsOpen(false);

          return undefined;
        }

        await delay(500);

        const response =
          await CommonAttachmentService.getFilesDownloadLinksBySessionIdQuery({
            sessionId,
          });

        if (response.failure) {
          pushError(
            new Error(safeFormattedIntlString(messages.errorProcessingFile)),
          );

          setIsOpen(false);

          return undefined;
        }

        if (!response.value?.links?.length) {
          setIsOpen(false);

          return undefined;
        }

        const firstLink = response.value.links[0];

        const downloadResponse = await fetch(firstLink);

        const fileName = decodeURI(
          downloadResponse.headers.get('x-ms-meta-originalname') ?? name,
        );
        const blob = await downloadResponse.blob();

        const file = new File([blob], fileName, {
          type: blob.type,
        });

        if ('failure' in file) {
          pushError(
            new Error(safeFormattedIntlString(messages.errorProcessingFile)),
          );
          setIsOpen(false);

          return undefined;
        }

        if (fileId) {
          if (file) {
            setValue(
              name,
              {
                value: file,
              },
              {
                shouldValidate: true,
              },
            );

            toast.success(
              safeFormattedIntlString(messages.fileUploadedSuccessfully),
            );
          }

          if (
            documentType === DOCUMENT_TYPES.NAME_PLATE &&
            file &&
            verifyThatShouldRecognizeSerialNumber({
              neededInformationItems,
              name,
              prefixFormName,
              claimIds,
              getValues,
            })
          ) {
            await processNamePlateForSerialNumber(file);
          }

          setIsOpen(false);

          return;
        } else {
          if (file) {
            setValue(
              name,
              {
                value: file,
              },
              {
                shouldValidate: true,
              },
            );

            toast.success(
              safeFormattedIntlString(messages.fileUploadedSuccessfully),
            );

            if (
              documentType === DOCUMENT_TYPES.NAME_PLATE &&
              file &&
              verifyThatShouldRecognizeSerialNumber({
                neededInformationItems,
                name,
                prefixFormName,
                claimIds,
                getValues,
              })
            ) {
              await processNamePlateForSerialNumber(blob);
            }
          }
        }

        setIsOpen(false);
      },
    );

  const allowedMimeTypes = Object.values(IMAGE_DOCUMENT_TYPES).includes(
    documentType as DOCUMENT_TYPES,
  )
    ? [MIME_TYPES.JPEG, MIME_TYPES.PNG, MIME_TYPES.IMAGE_HEIC, MIME_TYPES.PDF]
    : ALLOWED_MIME_TYPES;

  const isLoading = isUploadLoading || isProcessingNamePlate;

  if (isNewDesign) {
    return (
      <>
        {isImportByQrCode && !isMobileView && claimIds[0] && (
          <QrCodeImportModal
            isOpen={isOpen}
            additionalInformationId={additionalInformationId}
            claimId={claimIds[0]}
            onUploadFinishClick={handleUploadFinishClick}
            isSubmitLoading={isLoading}
            allowedMimeTypes={allowedMimeTypes}
          />
        )}

        <NewDesignFileUploadHookForm
          allowedMimeTypes={allowedMimeTypes}
          hollow
          name={name}
          label={label}
          postLabel={postLabel}
          onSelect={async (files) => {
            onChange(files);

            if (
              files &&
              files.length > 0 &&
              documentType === DOCUMENT_TYPES.NAME_PLATE &&
              verifyThatShouldRecognizeSerialNumber({
                neededInformationItems,
                name,
                prefixFormName,
                claimIds,
                getValues,
              })
            ) {
              await processNamePlateForSerialNumber(files[0]);
            }
          }}
          onRemove={onRemove}
          file={value}
          isRequired={isRequired}
          errors={errors}
          warnings={warnings}
          isLiveUpload={isLiveUpload}
          isFullWidth
          onImportByQrCodeClick={
            isImportByQrCode && !isMobileView
              ? () => {
                  setIsOpen(true);
                }
              : undefined
          }
          dataTestId={dataTestId}
        />
      </>
    );
  }

  return (
    <>
      {isImportByQrCode && !isMobileView && claimIds[0] && (
        <QrCodeImportModal
          isOpen={isOpen}
          additionalInformationId={additionalInformationId}
          claimId={claimIds[0]}
          onUploadFinishClick={handleUploadFinishClick}
          isSubmitLoading={isLoading}
          allowedMimeTypes={allowedMimeTypes}
        />
      )}

      <FileUploadHookForm
        allowedMimeTypes={allowedMimeTypes}
        hollow
        name={name}
        label={label}
        postLabel={postLabel}
        onSelect={async (files) => {
          onChange(files);

          if (
            files &&
            files.length > 0 &&
            documentType === DOCUMENT_TYPES.NAME_PLATE &&
            verifyThatShouldRecognizeSerialNumber({
              neededInformationItems,
              name,
              prefixFormName,
              claimIds,
              getValues,
            })
          ) {
            await processNamePlateForSerialNumber(files[0]);
          }
        }}
        onRemove={onRemove}
        file={value}
        isRequired={isRequired}
        errors={errors}
        warnings={warnings}
        isLiveUpload={isLiveUpload}
        isFullWidth
        onImportByQrCodeClick={
          isImportByQrCode && !isMobileView
            ? () => {
                setIsOpen(true);
              }
            : undefined
        }
        dataTestId={dataTestId}
      />
    </>
  );
};

export default AdditionalInformationFileInput;
